import { memo, useEffect, useState } from 'react';
import { openTagAdmin } from '@/pages/admin/TagAdminPage';
import { cn } from '@/lib/utils';

export interface TagData {
	id: number;
	name: string;
	color: string | null;
	icon: string | null;
	linkCount?: number;
	isPersonal?: boolean; // True if this is a personal tag (only visible to the user)
}

// Default colors by tag prefix
const PREFIX_COLORS: Record<string, string> = {
	'domain:': '#6366f1', // indigo - for domains
	'reddit:u:': '#ff4500', // reddit orange - for users
	'reddit:r:': '#ff4500', // reddit orange - for subreddits
	'platform:youtube': '#ff0000', // youtube red
	'platform:instagram': '#e1306c', // instagram pink
	'platform:twitter': '#1da1f2', // twitter blue
	'platform:tiktok': '#000000', // tiktok black
	'platform:onlyfans': '#00aff0', // onlyfans blue
	'platform:redgifs': '#e74c3c', // redgifs red
	'platform:pornhub': '#ff9000', // pornhub orange
	'platform:reddit': '#ff4500', // reddit orange
	'platform:fansly': '#00bcd4', // fansly cyan
	'platform:': '#8b5cf6', // purple for generic platform
	'category:': '#10b981', // emerald for categories
	'onlyfans:': '#00aff0', // onlyfans blue
	'fansly:': '#00bcd4', // fansly cyan
	'instagram:': '#e1306c', // instagram pink
	'twitter:': '#1da1f2', // twitter blue
	'tiktok:': '#000000', // tiktok black
	'youtube:': '#ff0000', // youtube red
	'pornhub:': '#ff9000', // pornhub orange
	'redgifs:': '#e74c3c', // redgifs red
	'coomer:': '#9b59b6', // coomer purple
	'hashtag:': '#3b82f6', // blue for hashtags
};

// Platform icons (emoji or unicode) - null/missing means no icon
const PLATFORM_ICONS: Record<string, string> = {
	'platform:youtube': '▶️',
	'platform:instagram': '📷',
	'platform:twitter': '𝕏',
	'platform:tiktok': '♪',
	'platform:onlyfans': '💙',
	'platform:redgifs': '🔴',
	'platform:pornhub': '🟠',
	'platform:reddit': '🔥',
	'platform:fansly': '💎',
	'reddit:u:': '👤',
	'reddit:r:': '📌',
	'onlyfans:creator:': '💙',
	'onlyfans:': '💙',
	'fansly:creator:': '💎',
	'fansly:': '💎',
	'instagram:user:': '📷',
	'instagram:': '📷',
	'twitter:user:': '𝕏',
	'twitter:': '𝕏',
	'tiktok:user:': '♪',
	'tiktok:': '♪',
	'youtube:channel:': '📺',
	'youtube:': '▶️',
	'pornhub:channel:': '🎬',
	'pornhub:star:': '⭐',
	'pornhub:': '🟠',
	'redgifs:user:': '🔴',
	'redgifs:': '🔴',
	'coomer:user:': '📦',
	'coomer:': '📦',
	// domain: intentionally no icon - just show domain text
	// category: intentionally no icon - just show category text
};

/**
 * Tag display priority:
 * 1. category: tags (alphabetically within)
 * 2. platform: tags (alphabetically within)
 * 3. user tags (*:user:* or *:u:* or *:r:*) (alphabetically within)
 * 4. domain: tags (alphabetically within)
 * 5. everything else (alphabetically)
 */
function getTagSortKey(name: string): [number, string] {
	const lower = name.toLowerCase();

	// Priority 1: category tags
	if (lower.startsWith('category:')) {
		return [1, name.slice('category:'.length)];
	}

	// Priority 2: platform tags
	if (lower.startsWith('platform:')) {
		return [2, name.slice('platform:'.length)];
	}

	// Priority 3: user tags (any platform with :user:, :u:, :r:, :creator:, :star:, :channel:)
	const userPrefixes = [':user:', ':u:', ':r:', ':creator:', ':star:', ':channel:'];
	for (const prefix of userPrefixes) {
		if (lower.includes(prefix)) {
			return [3, name];
		}
	}
	// Also handle direct platform usernames like onlyfans:username, fansly:username
	const directUserPlatforms = [
		'onlyfans:',
		'fansly:',
		'instagram:',
		'twitter:',
		'tiktok:',
		'redgifs:',
		'coomer:',
	];
	for (const prefix of directUserPlatforms) {
		if (lower.startsWith(prefix)) {
			return [3, name];
		}
	}

	// Priority 4: domain tags
	if (lower.startsWith('domain:')) {
		return [4, name.slice('domain:'.length)];
	}

	// Priority 5: everything else
	return [5, name];
}

/**
 * Sort tags for display using universal priority order
 */
export function sortTagsForDisplay(tags: TagData[]): TagData[] {
	return [...tags].sort((a, b) => {
		const [priorityA, nameA] = getTagSortKey(a.name);
		const [priorityB, nameB] = getTagSortKey(b.name);

		// First compare by priority
		if (priorityA !== priorityB) {
			return priorityA - priorityB;
		}

		// Then alphabetically within same priority
		return nameA.localeCompare(nameB);
	});
}

export function getDisplayTags(tags: TagData[], maxCount = 3): TagData[] {
	return sortTagsForDisplay(tags).slice(0, maxCount);
}

// Get default color based on tag prefix
function getDefaultColor(name: string): string {
	// Check specific prefixes first (longer matches)
	const sortedPrefixes = Object.keys(PREFIX_COLORS).sort((a, b) => b.length - a.length);
	for (const prefix of sortedPrefixes) {
		if (name.startsWith(prefix)) {
			return PREFIX_COLORS[prefix];
		}
	}
	return '#6b7280'; // gray-500 default
}

// Get icon for tag based on prefix
function getTagIcon(name: string, customIcon: string | null): string | null {
	if (customIcon) return customIcon;

	const sortedPrefixes = Object.keys(PLATFORM_ICONS).sort((a, b) => b.length - a.length);
	for (const prefix of sortedPrefixes) {
		if (name.startsWith(prefix)) {
			return PLATFORM_ICONS[prefix];
		}
	}
	return null;
}

// Tag formatting rules: prefix -> format function
// Format functions receive the value AFTER the prefix
type FormatFn = (value: string) => string;

const atUser: FormatFn = (v) => `@${v}`;
const asIs: FormatFn = (v) => v;
const capitalize: FormatFn = (v) => v.charAt(0).toUpperCase() + v.slice(1);
const toKebab: FormatFn = (v) => v.replace(/_/g, '-');
const redditSub: FormatFn = (v) => `r/${v}`;
const redditUser: FormatFn = (v) => `u/${v}`;
const hashtag: FormatFn = (v) => `#${v}`;
// Coomer format: "platform:username" -> "@username"
const coomerUser: FormatFn = (v) => {
	const parts = v.split(':');
	return parts.length >= 2 ? `@${parts[parts.length - 1]}` : `@${v}`;
};

// Sorted by prefix length descending (longer prefixes first for correct matching)
const TAG_FORMATTERS: [string, FormatFn][] = [
	// Coomer (archive) - format: coomer:user:platform:username
	['coomer:user:', coomerUser],
	// Reddit
	['reddit:r:', redditSub],
	['reddit:u:', redditUser],
	// OnlyFans
	['onlyfans:creator:', atUser],
	['onlyfans:user:', atUser],
	['onlyfans:', atUser],
	// Fansly
	['fansly:creator:', atUser],
	['fansly:', atUser],
	// Instagram
	['instagram:user:', atUser],
	['instagram:', atUser],
	// Twitter/X
	['twitter:user:', atUser],
	['twitter:', atUser],
	// TikTok
	['tiktok:user:', atUser],
	['tiktok:', atUser],
	// YouTube
	['youtube:channel:', asIs],
	// PornHub
	['pornhub:channel:', asIs],
	['pornhub:star:', asIs],
	// RedGifs
	['redgifs:user:', atUser],
	['redgifs:', atUser],
	// Generic
	['platform:', capitalize],
	['category:', toKebab],
	['domain:', asIs],
	['hashtag:', hashtag],
].sort((a, b) => b[0].length - a[0].length) as [string, FormatFn][];

// Smart format tag name for display
function formatTagName(name: string): string {
	for (const [prefix, format] of TAG_FORMATTERS) {
		if (name.startsWith(prefix)) {
			return format(name.slice(prefix.length));
		}
	}
	return name;
}

// Extract structured popover data from tag name
interface PopoverData {
	typeLabel: string; // "PLATFORM", "USER", "SUBREDDIT", "CATEGORY", "DOMAIN"
	platform: string | null; // "Reddit", "OnlyFans", etc. (null for category/domain)
	username: string | null;
	type: 'user' | 'channel' | 'subreddit' | 'category' | 'domain' | 'platform';
}

function getPopoverData(name: string): PopoverData {
	// Coomer format: coomer:user:platform:username
	if (name.startsWith('coomer:user:')) {
		const rest = name.slice('coomer:user:'.length);
		const parts = rest.split(':');
		return {
			typeLabel: 'ARCHIVE',
			platform: parts.length >= 2 ? `Coomer (${capitalize(parts[0])})` : 'Coomer',
			username: parts.length >= 2 ? parts[parts.length - 1] : parts[0],
			type: 'user',
		};
	}

	// Reddit user: reddit:u:username
	if (name.startsWith('reddit:u:')) {
		return {
			typeLabel: 'USER',
			platform: 'Reddit',
			username: name.slice('reddit:u:'.length),
			type: 'user',
		};
	}

	// Reddit subreddit: reddit:r:subreddit
	if (name.startsWith('reddit:r:')) {
		return {
			typeLabel: 'SUBREDDIT',
			platform: 'Reddit',
			username: name.slice('reddit:r:'.length),
			type: 'subreddit',
		};
	}

	// User patterns: platform:user:username or platform:creator:username or platform:username
	const userPatterns = [
		{ prefix: 'onlyfans:creator:', platform: 'OnlyFans' },
		{ prefix: 'onlyfans:user:', platform: 'OnlyFans' },
		{ prefix: 'onlyfans:', platform: 'OnlyFans' },
		{ prefix: 'fansly:creator:', platform: 'Fansly' },
		{ prefix: 'fansly:', platform: 'Fansly' },
		{ prefix: 'instagram:user:', platform: 'Instagram' },
		{ prefix: 'instagram:', platform: 'Instagram' },
		{ prefix: 'twitter:user:', platform: 'Twitter' },
		{ prefix: 'twitter:', platform: 'Twitter' },
		{ prefix: 'tiktok:user:', platform: 'TikTok' },
		{ prefix: 'tiktok:', platform: 'TikTok' },
		{ prefix: 'redgifs:user:', platform: 'RedGifs' },
		{ prefix: 'redgifs:', platform: 'RedGifs' },
	];

	for (const { prefix, platform } of userPatterns) {
		if (name.startsWith(prefix)) {
			return { typeLabel: 'USER', platform, username: name.slice(prefix.length), type: 'user' };
		}
	}

	// Channel patterns
	if (name.startsWith('youtube:channel:')) {
		return {
			typeLabel: 'CHANNEL',
			platform: 'YouTube',
			username: name.slice('youtube:channel:'.length),
			type: 'channel',
		};
	}
	if (name.startsWith('pornhub:channel:')) {
		return {
			typeLabel: 'CHANNEL',
			platform: 'PornHub',
			username: name.slice('pornhub:channel:'.length),
			type: 'channel',
		};
	}
	if (name.startsWith('pornhub:star:')) {
		return {
			typeLabel: 'STAR',
			platform: 'PornHub',
			username: name.slice('pornhub:star:'.length),
			type: 'user',
		};
	}

	// Platform tags: platform:name
	if (name.startsWith('platform:')) {
		const platformName = name.slice('platform:'.length);
		return {
			typeLabel: 'PLATFORM',
			platform: capitalize(platformName),
			username: null,
			type: 'platform',
		};
	}

	// Category tags: category:name
	if (name.startsWith('category:')) {
		return {
			typeLabel: 'CATEGORY',
			platform: null,
			username: name.slice('category:'.length).replace(/_/g, '-'),
			type: 'category',
		};
	}

	// Domain tags: domain:example.com
	if (name.startsWith('domain:')) {
		return {
			typeLabel: 'DOMAIN',
			platform: null,
			username: name.slice('domain:'.length),
			type: 'domain',
		};
	}

	// Hashtag tags: hashtag:travel
	if (name.startsWith('hashtag:')) {
		return {
			typeLabel: 'HASHTAG',
			platform: null,
			username: `#${name.slice('hashtag:'.length)}`,
			type: 'category',
		};
	}

	// Fallback
	return { typeLabel: 'TAG', platform: null, username: name, type: 'user' };
}

interface TagChipProps {
	tag: TagData;
	onClick?: (tag: TagData) => void;
	onRemove?: () => void;
	size?: 'sm' | 'md';
}

export const TagChip = memo(function TagChip({ tag, onClick, onRemove, size = 'sm' }: TagChipProps) {
	// Use tag's custom color, or fall back to prefix-based default color
	const baseColor = tag.color || getDefaultColor(tag.name);
	const icon = getTagIcon(tag.name, tag.icon);
	const displayName = formatTagName(tag.name);
	const popoverData = getPopoverData(tag.name);
	const isPersonal = tag.isPersonal ?? false;

	const chipClassName = cn(
		'inline-flex items-center gap-0.5 rounded-sm font-medium transition-colors',
		onClick && 'hover:brightness-90 cursor-pointer',
		size === 'sm' ? 'px-1 py-px text-[10px]' : 'px-1.5 py-0.5 text-xs'
	);

	// Personal tags get a dashed border to distinguish them
	const chipStyle = {
		backgroundColor: `${baseColor}20`,
		color: baseColor,
		border: isPersonal ? `1px dashed ${baseColor}80` : `1px solid ${baseColor}40`,
	};

	// Popover content - uses named group 'tag' to isolate hover from parent groups
	const popover = (
		<div
			className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg shadow-xl z-50 whitespace-nowrap pointer-events-none opacity-0 group-hover/tag:opacity-100 transition-opacity duration-150"
			style={{
				backgroundColor: `${baseColor}`,
				color: '#ffffff',
				minWidth: '100px',
				textAlign: 'center',
			}}
		>
			{/* Arrow */}
			<div
				className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
				style={{
					borderLeft: '6px solid transparent',
					borderRight: '6px solid transparent',
					borderTop: `6px solid ${baseColor}`,
				}}
			/>
			{/* Personal indicator */}
			{isPersonal && (
				<div className="text-[8px] font-bold tracking-widest opacity-60 mb-0.5">🔒 PERSONAL</div>
			)}
			{/* Type label */}
			<div className="text-[9px] font-bold tracking-widest opacity-75 mb-0.5">
				{popoverData.typeLabel}
			</div>
			{/* Platform name */}
			{popoverData.platform && <div className="text-sm font-bold">{popoverData.platform}</div>}
			{/* Username/value */}
			{popoverData.username && (
				<div className="text-sm font-semibold opacity-90">
					{popoverData.type === 'user' ? '@' : popoverData.type === 'subreddit' ? 'r/' : ''}
					{popoverData.username}
				</div>
			)}
			{/* Link count - only show when > 1 (1 link is assumed) */}
			{tag.linkCount !== undefined && tag.linkCount > 1 && (
				<div className="text-xs font-medium mt-1 text-white">{tag.linkCount} links</div>
			)}
		</div>
	);

	// Show inline count for non-category tags when > 1
	const showInlineCount =
		tag.linkCount !== undefined && tag.linkCount > 1 && !tag.name.startsWith('category:');

	// Remove button (X) - rendered inside chip when onRemove is set
	const removeButton = onRemove && (
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				onRemove();
			}}
			className="ml-0.5 -mr-0.5 opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
			title="Remove"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 16 16"
				fill="currentColor"
				className={size === 'sm' ? 'size-2.5' : 'size-3'}
			>
				<path d="M5.28 4.22a.75.75 0 0 0-1.06 1.06L6.94 8l-2.72 2.72a.75.75 0 1 0 1.06 1.06L8 9.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L9.06 8l2.72-2.72a.75.75 0 0 0-1.06-1.06L8 6.94 5.28 4.22Z" />
			</svg>
		</button>
	);

	// ALT+click handler: open tag admin editor
	function handleAltClick(e: React.MouseEvent) {
		if (e.altKey) {
			e.stopPropagation();
			e.preventDefault();
			openTagAdmin(tag.id);
			return true;
		}
		return false;
	}

	// Render as span when non-interactive, button when clickable
	// Uses named group 'tag' to prevent parent group hover from triggering all popovers
	if (!onClick) {
		return (
			<span className="relative inline-block group/tag">
				<span
					className={chipClassName}
					style={chipStyle}
					onClick={(e) => handleAltClick(e)}
					role="button"
					tabIndex={-1}
				>
					{isPersonal && <span className="opacity-60 text-[8px]">🔒</span>}
					{icon && <span className="opacity-80">{icon}</span>}
					<span>{displayName}</span>
					{showInlineCount && (
						<span className="opacity-50 text-[10px] ml-0.5">({tag.linkCount})</span>
					)}
					{removeButton}
				</span>
				{popover}
			</span>
		);
	}

	return (
		<span className="relative inline-block group/tag">
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					if (handleAltClick(e)) return;
					onClick(tag);
				}}
				className={chipClassName}
				style={chipStyle}
			>
				{isPersonal && <span className="opacity-60 text-[8px]">🔒</span>}
				{icon && <span className="opacity-80">{icon}</span>}
				<span>{displayName}</span>
				{showInlineCount && (
					<span className="opacity-50 text-[10px] ml-0.5">({tag.linkCount})</span>
				)}
				{removeButton}
			</button>
			{popover}
		</span>
	);
});

// Hook to track if shift key is held
function useShiftKey(): boolean {
	const [shiftHeld, setShiftHeld] = useState(false);

	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Shift') setShiftHeld(true);
		}
		function handleKeyUp(e: KeyboardEvent) {
			if (e.key === 'Shift') setShiftHeld(false);
		}
		// Also reset on blur (user switches windows)
		function handleBlur() {
			setShiftHeld(false);
		}

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);
		window.addEventListener('blur', handleBlur);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
			window.removeEventListener('blur', handleBlur);
		};
	}, []);

	return shiftHeld;
}

interface TagListProps {
	tags: TagData[];
	onTagClick?: (tag: TagData) => void;
	maxDisplay?: number;
	showAll?: boolean;
	size?: 'sm' | 'md';
	/** Hide domain: tags unless shift is held (default: true) */
	hideDomainTags?: boolean;
	/** Show shift indicator when domain tags are hidden (default: true) */
	showHiddenIndicator?: boolean;
}

export function TagList({
	tags,
	onTagClick,
	maxDisplay = 3,
	showAll = false,
	size = 'sm',
	hideDomainTags = true,
	showHiddenIndicator = true,
}: TagListProps) {
	const shiftHeld = useShiftKey();

	// Filter out domain: tags unless shift is held
	const visibleTags =
		hideDomainTags && !shiftHeld ? tags.filter((t) => !t.name.startsWith('domain:')) : tags;
	const hiddenDomainCount = tags.length - visibleTags.length;

	const displayTags = showAll ? visibleTags : getDisplayTags(visibleTags, maxDisplay);
	const remainingCount = visibleTags.length - displayTags.length;

	return (
		<div className="flex flex-wrap gap-1 items-center">
			{displayTags.map((tag) => (
				<TagChip key={tag.id} tag={tag} onClick={onTagClick} size={size} />
			))}
			{remainingCount > 0 && (
				<span className={cn('text-muted-foreground', size === 'sm' ? 'text-[10px]' : 'text-xs')}>
					+{remainingCount} more
				</span>
			)}
			{showHiddenIndicator && hiddenDomainCount > 0 && !shiftHeld && (
				<span
					className={cn(
						'text-muted-foreground/50 italic',
						size === 'sm' ? 'text-[9px]' : 'text-[10px]'
					)}
					title="Hold Shift to show domain tags"
				>
					⇧+{hiddenDomainCount}
				</span>
			)}
		</div>
	);
}
